import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Reusable delete confirmation dialog. Controlled via `open` + `onOpenChange`.
 *
 * Props:
 * - open (bool)
 * - onOpenChange (fn)
 * - onConfirm (fn)             — fires when user hits Delete
 * - title (string)
 * - recordLabel (string)       — shown in bold so user knows exactly what they're deleting
 * - description (string)       — extra text (default warns "cannot be undone")
 * - softDelete (bool)          — if true, describes archive-and-restore behavior
 * - loading (bool)
 * - confirmLabel (string)
 * - testIdPrefix (string)
 */
export default function ConfirmDelete({
  open,
  onOpenChange,
  onConfirm,
  title = "Are you sure?",
  recordLabel = "",
  description = "",
  softDelete = false,
  loading = false,
  confirmLabel = "Delete",
  testIdPrefix = "confirm-delete",
}) {
  const finalDesc =
    description ||
    (softDelete
      ? "This record will be archived. An Admin can restore it from the Audit Log if needed."
      : "This action cannot be undone. The record and its links will be permanently removed.");

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={`${testIdPrefix}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {recordLabel ? (
              <>
                You are about to delete <span className="font-semibold text-slate-900">{recordLabel}</span>.
                <br />
              </>
            ) : null}
            {finalDesc}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testIdPrefix}-cancel-btn`}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testIdPrefix}-confirm-btn`}
            disabled={loading}
            onClick={(e) => {
              e.preventDefault();
              onConfirm?.();
            }}
            className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-500"
          >
            {loading ? "Deleting…" : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
